import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithPopup,
  GoogleAuthProvider,
  User,
  UserCredential,
  Auth
} from 'firebase/auth';
import { initializeFirebase } from '../firebase';
import { AuthContextType, EmailCheckResult } from '../types';
import { initializeErrorLogger, setErrorLoggerUser, clearErrorLoggerUser } from '../services/errorLogger';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5555';

// Session timeout configuration
const SESSION_DURATION_MS = 4 * 60 * 60 * 1000; // 4 hours in milliseconds
const SESSION_CHECK_INTERVAL_MS = 60 * 1000; // Check every minute
const SESSION_TIMESTAMP_KEY = 'litrlly_session_timestamp';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [auth, setAuth] = useState<Auth | null>(null);
  const [initError, setInitError] = useState<string | null>(null);
  const [sessionExpired, setSessionExpired] = useState<boolean>(false);
  const sessionCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Check if session is expired
  const isSessionExpired = useCallback((): boolean => {
    const timestamp = localStorage.getItem(SESSION_TIMESTAMP_KEY);
    if (!timestamp) return false;

    const sessionStart = parseInt(timestamp, 10);
    const now = Date.now();
    return now - sessionStart > SESSION_DURATION_MS;
  }, []);

  // Start a new session (store timestamp)
  const startSession = useCallback(() => {
    localStorage.setItem(SESSION_TIMESTAMP_KEY, Date.now().toString());
  }, []);

  // Clear session timestamp
  const clearSession = useCallback(() => {
    localStorage.removeItem(SESSION_TIMESTAMP_KEY);
  }, []);

  // Clear session expired state (for UI to reset after showing message)
  const clearSessionExpired = useCallback(() => {
    setSessionExpired(false);
  }, []);

  // Handle session expiration
  const handleSessionExpired = useCallback(async (firebaseAuth: Auth) => {
    console.log('Session expired - logging out user');
    setSessionExpired(true);
    clearSession();
    try {
      await signOut(firebaseAuth);
    } catch (error) {
      console.error('Error signing out expired session:', error);
    }
  }, [clearSession]);

  // Initialize Firebase on mount
  useEffect(() => {
    const init = async () => {
      try {
        const { auth: firebaseAuth } = await initializeFirebase();
        setAuth(firebaseAuth);

        // Initialize error logger now that Firebase is ready
        await initializeErrorLogger();

        // Listen for auth state changes
        const unsubscribe = onAuthStateChanged(firebaseAuth, (user) => {
          if (user) {
            // User is signed in - check if session is expired
            if (isSessionExpired()) {
              handleSessionExpired(firebaseAuth);
            } else {
              // If no session timestamp exists (e.g., page refresh), start a new session
              const timestamp = localStorage.getItem(SESSION_TIMESTAMP_KEY);
              if (!timestamp) {
                startSession();
              }
              setCurrentUser(user);
              // Set user info for error tracking
              setErrorLoggerUser(user.uid, user.email || undefined);
            }
          } else {
            setCurrentUser(null);
            // Clear user info from error tracking
            clearErrorLoggerUser();
          }
          setLoading(false);
        });

        // Set up periodic session check
        sessionCheckIntervalRef.current = setInterval(() => {
          if (firebaseAuth.currentUser && isSessionExpired()) {
            handleSessionExpired(firebaseAuth);
          }
        }, SESSION_CHECK_INTERVAL_MS);

        return () => {
          unsubscribe();
          if (sessionCheckIntervalRef.current) {
            clearInterval(sessionCheckIntervalRef.current);
          }
        };
      } catch (error) {
        console.error('Firebase initialization error:', error);
        setInitError((error as Error).message);
        setLoading(false);
      }
    };

    init();
  }, [isSessionExpired, handleSessionExpired, startSession]);

  // Check if email is allowed (calls backend)
  // Now always returns allowed: true, but includes isVip flag for informational purposes
  const checkEmailAllowed = async (email: string): Promise<EmailCheckResult> => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/check-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      const data: EmailCheckResult = await response.json();
      return data;
    } catch (error) {
      console.error('Email check error:', error);
      return { allowed: false, error: 'Failed to validate email', isVip: false };
    }
  };

  // Sign up with email and password
  // Registration is open to everyone - VIP status is determined server-side during subscription initialization
  const signup = async (email: string, password: string): Promise<UserCredential> => {
    if (!auth) throw new Error('Firebase not initialized');

    const result = await createUserWithEmailAndPassword(auth, email, password);
    startSession(); // Start session timer on signup

    return result;
  };

  // Login with email and password
  const login = async (email: string, password: string): Promise<UserCredential> => {
    if (!auth) throw new Error('Firebase not initialized');
    const result = await signInWithEmailAndPassword(auth, email, password);
    startSession(); // Start session timer on login
    return result;
  };

  // Logout
  const logout = async (): Promise<void> => {
    if (!auth) throw new Error('Firebase not initialized');
    clearSession(); // Clear session timestamp on logout
    return signOut(auth);
  };

  // Reset password
  const resetPassword = (email: string): Promise<void> => {
    if (!auth) throw new Error('Firebase not initialized');
    return sendPasswordResetEmail(auth, email);
  };

  // Sign in with Google
  const signInWithGoogle = async (): Promise<UserCredential> => {
    if (!auth) throw new Error('Firebase not initialized');
    const provider = new GoogleAuthProvider();
    const result = await signInWithPopup(auth, provider);
    startSession(); // Start session timer on Google sign-in
    return result;
  };

  const value: AuthContextType = {
    currentUser,
    loading,
    signup,
    login,
    logout,
    resetPassword,
    signInWithGoogle,
    checkEmailAllowed,
    initError,
    sessionExpired,
    clearSessionExpired
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
